/**
 * useReviewAgent — React hook that polls the review_agent sidecar
 * for audit + review data for a given (session, module).
 *
 * SRP: this hook ONLY handles the data lifecycle (when to fetch,
 * how often to poll, how to cancel, how to expose state to a
 * component). It does NOT render UI or know about Word.
 *
 * The hook is IA-agnostic — classic tabbed UI and chat-first both
 * call it the same way. Whichever card composes the returned state
 * renders the appropriate visual.
 *
 * Polling strategy:
 *   - First mount: fetch immediately
 *   - Loop at ``pollIntervalMs`` (default 3s) until the audit
 *     record exists (it appears after the sidecar's watcher
 *     processes the module log — usually <1s after the pipeline
 *     writes the log)
 *   - Once audit exists, slow to ``settledPollMs`` (default 30s)
 *     so we catch re-reviews triggered by POST /trigger without
 *     burning CPU in steady state
 *   - Unmount: abort in-flight request via AbortController
 *
 * Caller gets:
 *   - ``audit``     : the current AuditRecord or null (not yet produced)
 *   - ``review``    : per-persona scorecards + aggregated verdict, or null
 *   - ``loading``   : true while the FIRST fetch is in flight
 *   - ``error``     : non-null when the most recent fetch failed;
 *                     cleared on the next successful fetch
 *
 * Spec: docs/specs/10-review-agent-sidecar.md § HTTP surface.
 */

import { useEffect, useRef, useState } from "react";
import type { AuditRecord, ReviewResponse } from "../components/review-agent/types";
import { ReviewAgentFetchError, fetchAudit, fetchReview } from "../services/reviewAgentClient";

interface UseReviewAgentParams {
  /** Sidecar session id, e.g. "DEMO_CSR__smoke-session". */
  session: string | null;
  /** Module id or sentinel ("_document" / "_finalization"). */
  target: string | null;
  /** Poll interval before the first audit record appears (ms). */
  pollIntervalMs?: number;
  /** Poll interval after the audit record exists (ms). */
  settledPollMs?: number;
  /** If false, don't fetch at all (useful for paused views). */
  enabled?: boolean;
}

export interface UseReviewAgentState {
  audit: AuditRecord | null;
  review: ReviewResponse | null;
  loading: boolean;
  error: ReviewAgentFetchError | null;
  /** 404 means the sidecar hasn't produced the audit yet — not an error. */
  notYetProduced: boolean;
}

const DEFAULT_POLL_MS = 3_000;
const DEFAULT_SETTLED_MS = 30_000;

export function useReviewAgent(params: UseReviewAgentParams): UseReviewAgentState {
  const {
    session,
    target,
    pollIntervalMs = DEFAULT_POLL_MS,
    settledPollMs = DEFAULT_SETTLED_MS,
    enabled = true,
  } = params;

  const [state, setState] = useState<UseReviewAgentState>({
    audit: null,
    review: null,
    loading: true,
    error: null,
    notYetProduced: false,
  });

  // A ref to the latest audit so the poll loop can decide whether
  // to use the fast or settled cadence without recreating the
  // interval every render.
  const auditRef = useRef<AuditRecord | null>(null);

  useEffect(() => {
    // Reset when inputs change
    setState({
      audit: null,
      review: null,
      loading: true,
      error: null,
      notYetProduced: false,
    });
    auditRef.current = null;

    if (!enabled || !session || !target) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    async function doFetch(): Promise<void> {
      // Narrow session/target types for TypeScript inside the async closure.
      if (!session || !target) return;
      // Fetch audit + review in parallel. Either can 404
      // independently; we surface notYetProduced when both 404.
      const [auditResult, reviewResult] = await Promise.allSettled([
        fetchAudit(session, target, abort.signal),
        fetchReview(session, target, abort.signal),
      ]);

      if (stopped) return;

      const auditOk = auditResult.status === "fulfilled" ? auditResult.value : null;
      const reviewOk = reviewResult.status === "fulfilled" ? reviewResult.value : null;

      // A 404 on audit means "not yet produced"; any other error
      // is a real problem.
      let err: ReviewAgentFetchError | null = null;
      let notYet = false;
      if (auditResult.status === "rejected") {
        const e = auditResult.reason;
        if (e instanceof ReviewAgentFetchError && e.status === 404) {
          notYet = true;
        } else if (e instanceof ReviewAgentFetchError) {
          err = e;
        } else {
          err = new ReviewAgentFetchError(0, "<audit>", String(e));
        }
      }
      // Review 404 is expected when audit 404s; don't double-flag.
      if (
        !notYet &&
        auditOk &&
        reviewResult.status === "rejected" &&
        reviewResult.reason instanceof ReviewAgentFetchError &&
        reviewResult.reason.status !== 404
      ) {
        err = reviewResult.reason;
      }

      auditRef.current = auditOk;
      setState({
        audit: auditOk,
        review: reviewOk,
        loading: false,
        error: err,
        notYetProduced: notYet,
      });
    }

    function scheduleNext(): void {
      if (stopped) return;
      const interval = auditRef.current ? settledPollMs : pollIntervalMs;
      timer = setTimeout(async () => {
        try {
          await doFetch();
        } catch {
          // doFetch already catches its own errors; this is a
          // safety net.
        }
        scheduleNext();
      }, interval);
    }

    // Kick off the first fetch immediately, then schedule the loop.
    doFetch().then(() => {
      if (!stopped) scheduleNext();
    });

    return () => {
      stopped = true;
      abort.abort();
      if (timer) clearTimeout(timer);
    };
  }, [session, target, pollIntervalMs, settledPollMs, enabled]);

  return state;
}
